/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ShopperProducts } from '@/scapi';
import ImageGallery from '@/components/image-gallery';
// RGBARRY-CUSTOMIZATION: mobile PDP wishlist heart overlays the hero image (see heroOverlay below)
import { WishlistButton } from '@/components/buttons/wishlist-button';
import ProductInfo from './product-info';
import ProductCartActions from '@/components/product-cart-actions';
import ProductViewProvider, { useProductView } from '@/providers/product-view';
import { useProductImages } from '@/hooks/product/use-product-images';
import { useSelectedVariations } from '@/hooks/product/use-selected-variations';
import { isProductSet, isProductBundle } from '@/lib/product/product-utils';
import { getPdpProductBadges } from '@/lib/product/product-badges';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { UITarget } from '@/targets/ui-target';
// RGBARRY-CUSTOMIZATION: Fibbl 3D/AR viewer
import FibblScriptLoader from '@/components/fibbl/fibbl-script-loader';
import type { FibblConfig } from '@/lib/fibbl.server';
import { useRouteLoaderData } from 'react-router';
import type { loader as rootLoader } from '@/root';
// END RGBARRY-CUSTOMIZATION

interface ProductViewProps {
    product: ShopperProducts.schemas['Product'];
    mode?: 'add' | 'edit';
    // RGBARRY-CUSTOMIZATION: Fibbl 3D/AR viewer config
    fibblConfig?: FibblConfig;
    // END RGBARRY-CUSTOMIZATION
}

/**
 * ProductView component renders a complete product detail view with image gallery and product information.
 *
 * @param props - The component props
 * @param props.product - The product data from Salesforce Commerce Cloud containing all product details,
 *                        variants, pricing, and metadata
 *
 * @returns A React element containing the complete product view layout
 *
 * @example
 * ```tsx
 * <ProductView product={productData} />
 * ```
 */
function StickyTray({
    product,
    thumbSrc,
}: {
    product: ShopperProducts.schemas['Product'];
    thumbSrc: string | undefined;
}) {
    const { canAddToCart, isAddingToOrUpdatingCart, handleAddToCart } = useProductView();

    const handleClick = () => {
        if (typeof navigator !== 'undefined' && 'vibrate' in navigator) navigator.vibrate(30);
        void handleAddToCart();
    };

    if (typeof document === 'undefined') return null;

    return createPortal(
        <div className="fixed inset-x-0 bottom-0 z-50 bg-background border-t border-[#e8e4e0] shadow-[0_-4px_24px_rgba(0,0,0,0.08)] pb-[env(safe-area-inset-bottom,0px)]">
            <div className="mx-auto max-w-4xl px-4 lg:px-6 py-3 flex items-center gap-4">
                {/* Left — thumb + name + price */}
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    {thumbSrc && (
                        <img
                            src={thumbSrc}
                            alt=""
                            aria-hidden="true"
                            className="w-12 h-12 object-contain bg-[#F4F4F4] shrink-0"
                        />
                    )}
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-[#1a1a1a] truncate leading-tight">{product.name}</p>
                        {product.price != null && <p className="text-sm text-[#555]">${product.price.toFixed(2)}</p>}
                    </div>
                </div>
                {/* Right — ATC button */}
                <button
                    onClick={handleClick}
                    disabled={!canAddToCart || isAddingToOrUpdatingCart}
                    className="shrink-0 bg-[#1a1a1a] hover:bg-[#333] disabled:opacity-40 text-primary-foreground text-xs tracking-[0.1em] uppercase font-medium px-8 py-3 rounded-none transition-colors duration-200 cursor-pointer disabled:cursor-not-allowed">
                    {isAddingToOrUpdatingCart ? 'Adding…' : 'Add to Cart'}
                </button>
            </div>
        </div>,
        document.body
    );
}

export default function ProductView({ product, fibblConfig }: ProductViewProps): ReactElement {
    const isProductASet = isProductSet(product);
    const isProductABundle = isProductBundle(product);
    // RGBARRY-CUSTOMIZATION: nonce for Fibbl script CSP
    const rootData = useRouteLoaderData<typeof rootLoader>('root');
    const nonce = rootData?.nonce ?? undefined;
    // END RGBARRY-CUSTOMIZATION

    const selectedAttributes = useSelectedVariations({ product });
    const { galleryImages: rawGalleryImages } = useProductImages({ product, selectedAttributes, viewType: 'hi-res' });

    // RGBARRY-CUSTOMIZATION: inject Fibbl and Vimeo tiles into gallery slot positions
    const galleryImages = useMemo(() => {
        if (rawGalleryImages.length === 0) return rawGalleryImages;
        const vimeoId = (product as unknown as { c_vimeoVideoId?: string }).c_vimeoVideoId;
        // Fibbl tile only renders when the site pref is on AND this specific product has a model
        const productFibblEnabled = (product as unknown as { c_fibblEnabled?: boolean }).c_fibblEnabled === true;
        const showFibbl = fibblConfig?.enabled && productFibblEnabled;
        if (!showFibbl) {
            if (!vimeoId) return rawGalleryImages;
            return [
                rawGalleryImages[0],
                { type: 'video' as const, vimeoId, src: '', alt: `${product.name ?? ''} video` },
                ...rawGalleryImages.slice(1),
            ];
        }
        // Slot 0: hero (LCP), Slot 1: Fibbl 3D, Slot 2: Vimeo when present
        // Resolve to variant ID when attributes are fully selected — Fibbl indexes models per variant
        const selectedVariant = product.variants?.find((v) => {
            const values = v.variationValues;
            return values && Object.entries(selectedAttributes).every(([k, val]) => values[k] === val);
        });
        // Only show Fibbl when the loaded product matches the selected variant.
        // During color-switch navigation, selectedAttributes updates before the new
        // product loads — suppress the tile to avoid a flash of "Loading 3D" for a
        // variant that has no model.
        const fibblVariantId = selectedVariant?.productId ?? product.id ?? '';
        if (fibblVariantId !== product.id) {
            if (!vimeoId) return rawGalleryImages;
            return [
                rawGalleryImages[0],
                { type: 'video' as const, vimeoId, src: '', alt: `${product.name ?? ''} video` },
                ...rawGalleryImages.slice(1),
            ];
        }
        const fibblEntry = {
            type: 'fibbl' as const,
            src: '',
            fibblProductId: fibblVariantId,
            alt: `${product.name ?? ''} 3D view`,
        };
        if (vimeoId) {
            return [
                rawGalleryImages[0],
                fibblEntry,
                { type: 'video' as const, vimeoId, src: '', alt: `${product.name ?? ''} video` },
                ...rawGalleryImages.slice(1),
            ];
        }
        return [rawGalleryImages[0], fibblEntry, ...rawGalleryImages.slice(1)];
    }, [rawGalleryImages, product, fibblConfig, selectedAttributes]);
    // END RGBARRY-CUSTOMIZATION

    // Track whether the buy-box card is off-screen
    const buyBoxRef = useRef<HTMLDivElement>(null);
    const [showTray, setShowTray] = useState(false);

    useEffect(() => {
        const el = buyBoxRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(([entry]) => setShowTray(!entry.isIntersecting), {
            threshold: 0,
            rootMargin: '0px',
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const thumbSrc = galleryImages[0]?.src;

    const config = useConfig();
    const badgeLabels = getPdpProductBadges({
        product: product as unknown as Record<string, unknown> & { promotions?: unknown[]; inStock?: boolean },
        badgeDetails: config.global.badges,
        maxBadges: 2,
    }).badges.map((b) => b.label);

    return (
        <ProductViewProvider product={product} mode="add">
            {/* RGBARRY-CUSTOMIZATION: Fibbl script loader — only when enabled via site pref */}
            {fibblConfig?.enabled && (
                <FibblScriptLoader nonce={nonce} token={fibblConfig.token} locale={fibblConfig.locale} />
            )}
            {/* END RGBARRY-CUSTOMIZATION */}
            {/* Wide image left, sticky card buy-box right */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] xl:grid-cols-[1fr_420px] gap-4 lg:gap-10 items-start">
                {/* Left Column */}
                <div className="order-1">
                    <ImageGallery
                        key={product.id}
                        images={galleryImages}
                        eager={!isProductASet && !isProductABundle}
                        tileGrid
                        showNavigationArrows
                        navigationArrowSize="lg"
                        // RGBARRY-CUSTOMIZATION: mobile PDP hero arrows sit directly on the image with no
                        // background/shadow chrome (matches the bare-outline heart overlay below).
                        navigationArrowClassName="bg-transparent hover:bg-transparent shadow-none"
                        productName={product.name}
                        firstImageBadges={badgeLabels}
                        // RGBARRY-CUSTOMIZATION: mobile-only heart overlaid top-right of the hero image, styled
                        // as a bare outline icon (no background/shadow); desktop keeps the heart in the buy-box below.
                        heroOverlay={
                            <WishlistButton
                                product={{
                                    productId: product.id,
                                    productName: product.name,
                                    price: product.price,
                                    image: product.imageGroups?.[0]?.images?.[0],
                                }}
                                surface="pdp"
                                size="sm"
                                className="bg-transparent shadow-none"
                            />
                        }
                    />
                    <UITarget targetId="sfcc.pdp.agent.productHelper" />
                </div>

                {/* RGBARRY-CUSTOMIZATION: sticky white card buy-box */}
                <div ref={buyBoxRef} className="order-2 lg:sticky lg:top-24">
                    <div className="bg-background p-4 lg:p-6">
                        {/* RGBARRY-CUSTOMIZATION: hide standalone qty picker; qty is set via the in-cart stepper */}
                        <ProductInfo product={product} hideQuantityPicker />
                        <ProductCartActions product={product} />
                        <UITarget targetId="sfcc.pdp.returnsWarranty" />
                        {/* @sfdc-extension-block-start SFDC_EXT_SHIPPING_DELIVERY */}
                        <UITarget targetId="sfcc.pdp.estimatedDelivery" />
                        {/* @sfdc-extension-block-end SFDC_EXT_SHIPPING_DELIVERY */}
                        <UITarget targetId="sfcc.pdp.faq" />
                        <UITarget targetId="sfcc.pdp.collapsibles" />
                    </div>
                </div>
            </div>

            {/* Sticky bottom tray — appears when buy-box scrolls off screen */}
            {showTray && !isProductASet && !isProductABundle && <StickyTray product={product} thumbSrc={thumbSrc} />}
        </ProductViewProvider>
    );
}
